import { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { getCurrentLayout, getPrefLangSchedule } from '../js/layoutSelector';
import { resetDeviceConfig, setCurrentLayout } from '../Redux/Reducers/deviceConfigReducer';
import { mapFloatingWidgetsToNavItem, mapWidgetsToNavItem, navItemsConstant } from '../js/getNavItems';
import { setHcommerceStoreSkuData } from '../Redux/Reducers/hcommerceReducer';
import { setRenderApp, setServiceStatus } from '../Redux/Reducers/appConfigReducer';
import { checkPreCheckInWidgetState } from '../js/preCheckInWidgetState';
import { updateDynamicStyle } from '../Redux/Reducers/dynamicStyleSheetReducer';
import { getCleanUrl } from '../utils/Helper';
import { resetHotelLang } from '../Redux/Reducers/hotelLangReducer';
import { resetServerDateTime } from '../Redux/Reducers/serverDateTimeReducer';
import { ACTIVITIES_DEMO_DATA_URL, DEMO_FLOW_ENABLED } from '../config/Constant';
import { updateActivities } from '../Redux/Reducers/demoDataReducer';

/**
 * Custom hook to manage layout mapping and CSS loading
 * Handles current layout selection based on schedule and language preferences
 */
export const useLayoutManager = () => {
    const dispatch = useDispatch();

    const {
        canProceedRenderApp,
        webAppWidgetsInfo,
        serverTimeDataObj,
        hotelLanguages,
        isLoggedIn,
        gotGuestDetailsResponse,
        currentLayoutId,
        layoutCssData,
        error,
        errorMessage
    } = useSelector(
        state => ({
            canProceedRenderApp: state.appConfigSlice.canProceedRenderApp,
            webAppWidgetsInfo: state.deviceConfigSlice.webAppWidgetsInfo,
            serverTimeDataObj: state.serverDateTimeSlice.serverTimeDataObj,
            hotelLanguages: state.hotelLangSlice.hotelLanguages,
            isLoggedIn: state.appConfigSlice.isLoggedIn,
            gotGuestDetailsResponse: state.guestDetailsSlice.gotGuestDetailsResponse,
            currentLayoutId: state.deviceConfigSlice.currentLayoutId,
            layoutCssData: state.appConfigSlice.layoutCssData,

            error: state.deviceConfigSlice.error || state.hotelLangSlice.error || state.serverDateTimeSlice.error,
            errorMessage: state.deviceConfigSlice.errorMessage || state.hotelLangSlice.errorMessage || state.serverDateTimeSlice.errorMessage
        }),
        shallowEqual
    );



    /**
     * Fetches and applies custom CSS for the current layout
    */
    const fetchLayoutCSS = async () => {
        if (currentLayoutId && layoutCssData?.length) {
            for (const cssData of layoutCssData) {
                if (cssData.layoutId == currentLayoutId) {
                    try {
                        const response = await fetch(getCleanUrl(cssData.customCSSFile));
                        const cssJson_data = await response.json();

                        console.log("CSS data:", cssJson_data);
                        dispatch(updateDynamicStyle(cssJson_data));
                    } catch (error) {
                        console.log("Error fetching CSS file:", error);
                    }
                }
            }
        }
    };

    // Fetch custom CSS when layout changes
    useEffect(() => {
        fetchLayoutCSS();
        if(DEMO_FLOW_ENABLED){
            fetchActivitiesDemoData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLayoutId, layoutCssData]);

    
    /**\
     * load demo data for activities and spa
     */
    const fetchActivitiesDemoData = async () => {
        const fileNames = [
            "fetchBooking.json",
            "getEvents.json",
            "getAvailabilityEvent.json",
            "getAvailabilityExperience.json",
            "getAvailabilitySubEvents.json",
            "getEventsInfoEvent.json",
            "getEventsInfoExperience.json",
            "getEventsInfoSubEvents.json",
        ];

        try {
            const responsesObject = await Promise.all(
                fileNames.map(async (fileName) => {
                    const response = await fetch(
                        getCleanUrl(`${ACTIVITIES_DEMO_DATA_URL}/${fileName}`)
                    );

                    if (!response.ok) {
                        throw new Error(`Failed to fetch ${fileName}`);
                    }

                    const json = await response.json();

                    return {
                        [fileName.replace(/\.json$/, "")]: json ?? null,
                    }; 
                })
            ).then((results) =>
                results.reduce((acc, curr) => ({ ...acc, ...curr }), {})
            );

            dispatch(updateActivities(responsesObject));
            console.log("Activities Response: ", responsesObject);
        } catch (error) {
            console.error("Activities Error: ", error);
        }
    };

    /**
     * Maps current layout based on schedule and language preferences
     */
    const mapCurrentLayout = async () => {
        const props = {
            dispatch,
            setHcommerceStoreSkuData,
            hotelLanguage: hotelLanguages,
            webAppWidgetsInfo,
            serverTimeDataObj
        };

        const currentSchedule = await getPrefLangSchedule(props);
        const currentLayout = await getCurrentLayout(props, currentSchedule);

        const getMappedWidgetsWithNavItem = mapWidgetsToNavItem(
            props,
            currentLayout.scrollableWidgets,
            navItemsConstant
        );

        const floatingWidgetsWithNavItem = mapFloatingWidgetsToNavItem(
            currentLayout.floatingWidgets,
            navItemsConstant
        );

        const mapCurrentLayoutWithNavItems = {
            ...currentLayout,
            scrollableWidgets: getMappedWidgetsWithNavItem,
            floatingWidgets: floatingWidgetsWithNavItem
        };

        dispatch(setCurrentLayout(mapCurrentLayoutWithNavItems));
        console.log("currentLayoutID------", currentLayout.durationId);

        // Minimal delay to ensure state is set before rendering
        setTimeout(() => {
            dispatch(setRenderApp(true));
        }, 10);
    };

    // Map layout when all required data is available
    useEffect(() => {
        const hasAllData =
            canProceedRenderApp &&
            Object.keys(webAppWidgetsInfo).length > 0 &&
            Object.keys(serverTimeDataObj).length > 0 &&
            Object.keys(hotelLanguages).length > 0 &&
            (isLoggedIn || gotGuestDetailsResponse);

        console.log("layout manager: ", canProceedRenderApp, webAppWidgetsInfo, serverTimeDataObj, hotelLanguages, isLoggedIn, gotGuestDetailsResponse);

        if(error && !hasAllData){
            dispatch(setServiceStatus({ hasError: true, errorMessage: errorMessage }));
            return
        }
        if (hasAllData) {
            mapCurrentLayout();
            dispatch(checkPreCheckInWidgetState());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, canProceedRenderApp, webAppWidgetsInfo, serverTimeDataObj, hotelLanguages, isLoggedIn, gotGuestDetailsResponse]);



    return null;
};
